﻿import React from 'react';
import PropTypes from 'prop-types';

import { useGetBranches } from 'features/users/user.query';
import Loader from 'components/Loader';

import Select from './Select';

const BranchSelect = React.forwardRef(({ userId, value, onChange, ...rest }, ref) => {
  const { data: branches, isLoading } = useGetBranches(userId, {
    enabled: !!userId,
  });

  // A failed request leaves data undefined while isLoading is false, and `.length`
  // on it threw, taking the login screen down rather than showing the fallback.
  const choices = branches?.length
    ? branches.map(({ name, id }) => ({
        title: name,
        value: id.toString(),
      }))
    : [
        {
          title: 'No branches found',
          value: null,
        },
      ];

  // The first branch is preselected. FormItem registers through react-hook-form's
  // Controller, which starts the field at '', and a <select> paints its first option
  // regardless, so without this the modal showed "Online" while the form held nothing
  // and Get In failed with "Please select an application". Telling the form what the
  // user can already see is what makes the visible default real.
  //
  // `value` drives the display immediately so there is no frame showing a different
  // option, while the effect writes the same value into form state for validation.
  // The fallback choice carries a null value, so nothing is selected when the request
  // failed and the field stays invalid, which is correct.
  const firstValue = choices[0]?.value;

  React.useEffect(() => {
    if (isLoading || value || firstValue == null) {
      return;
    }
    onChange?.({ target: { value: firstValue } });
  }, [isLoading, value, firstValue, onChange]);

  if (isLoading) {
    return <Loader />;
  }

  // Controlled only when something is driving it, which is how FormItem always uses
  // it. Passing a value with no onChange makes React warn and freezes the field, so
  // standalone usage stays uncontrolled and just starts on the same first branch.
  const selection = onChange
    ? { value: value || firstValue || '', onChange }
    : { defaultValue: firstValue ?? undefined };

  return (
    <Select
      data-testid="branch_select"
      ref={ref}
      choices={choices}
      placeholder="Select an application"
      {...selection}
      {...rest}
    />
  );
});

BranchSelect.propTypes = {
  userId: PropTypes.number.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default BranchSelect;
